import React, { useContext, useState } from "react"
import { Button, Col, Div, Row, Text } from "atomize"
import { toast } from "react-toastify"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import Container from "../atoms/Container"
import Chip from "../atoms/Chip"
import Paragraph from "../atoms/Paragraph"
import TextInput from "../inputs/TextInput"
import LessonContentFields from "../lesson/LessonContentFields"
import SchemaEditor from "../lesson/schema/SchemaEditor"
import { CourtIcon, SchemaCard } from "../lesson/schema/ExerciseSchema"
import NotebookRemoveConfirm from "../notebook/NotebookRemoveConfirm"
import NotebookUpdate from "../notebook/NotebookUpdate"
import { LevelChips } from "../notebook/NotebookVariants"
import { theme } from "../settings/config"
import { notebookContext } from "../providers/notebook.provider"
import { profileContext } from "../providers/profile.provider"
import {
	addNotebookEntry,
	deleteNotebookEntry,
	editNotebookEntry,
} from "../utils/firestore.utils"
import { networkAwareMessage } from "../utils/network.utils"
import {
	EMPTY_LESSON_CONTENT,
	PHASES,
	isLessonContentEmpty,
} from "../utils/lessonContent.utils"
import {
	NOTEBOOK_EXERCISE,
	NOTEBOOK_LESSON,
	NOTEBOOK_NOTE_MAX,
	NOTEBOOK_TEXT_MAX,
	NOTEBOOK_TITLE_MAX,
	isMine,
	levelLabel,
	notebookUpdate,
	phaseSchemas,
	phaseSources,
	usageLabel,
	usedLevels,
	variantOf,
	withNewVariant,
	withNotebookUpdate,
	withVariant,
	withoutVariant,
} from "../utils/notebook.utils"

// Una voce del quaderno, nuova o da cambiare: il nome e, per una lezione,
// il programma intero; per un esercizio la fase — o nessuna, se va bene per
// tutte —, il testo e lo schema. Cambiarla non cambia le lezioni che l'hanno già
// usata: ci è stata copiata.
//
// Quello che si cambia è una variante (`variantID`): il suo livello, cosa
// cambia rispetto alle altre e quello che c'è dentro; il nome e la fase sono
// della voce. Con `asNewVariant` il foglio ne fa una nuova, che parte dalla
// variante `variantID` e vuole un livello che non abbia già la sua.
export function NotebookEntryModal({ closeModal, entryID, variantID, asNewVariant = false, kind: newKind }) {
	const { coach } = useContext(profileContext)
	const { getEntry } = useContext(notebookContext)

	const saved = entryID ? getEntry(entryID) : null
	const base = saved ? (variantOf(saved, variantID) ?? saved.variants[0]) : null
	const kind = saved?.kind ?? newKind ?? NOTEBOOK_LESSON
	const isExercise = kind === NOTEBOOK_EXERCISE
	const isNewVariant = Boolean(saved && asNewVariant)

	const [title, setTitle] = useState(saved?.title ?? "")
	const [content, setContent] = useState(() => base?.content ?? EMPTY_LESSON_CONTENT)
	const [phase, setPhase] = useState(saved?.phase ?? null)
	const [text, setText] = useState(base?.text ?? "")
	const [schema, setSchema] = useState(base?.schema ?? null)
	const [level, setLevel] = useState(isNewVariant ? null : (base?.level ?? null))
	const [note, setNote] = useState(isNewVariant ? "" : (base?.note ?? ""))
	// l'editor aperto, e la miniatura da cui cresce se c'è: `{ origin }`
	const [drawing, setDrawing] = useState(null)
	const [isSaving, setSavingState] = useState(false)
	// "Togli dal quaderno" chiede conferma qui dentro, al posto del modulo:
	// un altro foglio chiuderebbe questo, e con "No" si perderebbe quello
	// che si stava cambiando
	const [confirmingRemoval, setConfirmingRemoval] = useState(false)

	// tolta da un altro telefono mentre questo la guardava, o di un collega
	if (entryID && (!saved || !isMine(saved)))
		return (
			<Container>
				<Text textSize="title" textWeight="bold" textColor={theme.colors.gunmetal}>
					Non è più nel quaderno
				</Text>
				<Div m={{ t: "1rem" }}>
					<PrimaryButton
						m="auto"
						bg={theme.colors.gunmetal_fill}
						textColor="white"
						textSize="subheader"
						text="Chiudi"
						action={closeModal}
					/>
				</Div>
			</Container>
		)

	const hasBody = isExercise ? text.trim() !== "" : !isLessonContentEmpty(content)
	// i livelli delle altre varianti: una per livello, e una sola senza
	const taken = saved ? usedLevels(saved, isNewVariant ? null : base.variantID) : []
	const canClearLevel = !isNewVariant && !taken.includes(null)
	const variantsCount = saved?.variants.length ?? 1
	const showNote = isNewVariant || variantsCount > 1
	const canSave =
		title.trim() !== "" &&
		hasBody &&
		(level !== null || canClearLevel || !saved) &&
		Boolean(coach?.coachID) &&
		!isSaving

	// Gli esercizi di una lezione del quaderno sono copie anche loro: se
	// l'esercizio, nel quaderno, è andato avanti, lo si riprende com'è adesso
	const exerciseNote = (phase, index) => {
		const current = content.phases[phase.id]
		const update = notebookUpdate(
			phaseSources(current)[index],
			current.exercises[index],
			phaseSchemas(current)[index],
			getEntry,
		)
		if (!update) return null
		return (
			<NotebookUpdate
				update={update}
				name={`esercizio ${index + 1}, ${phase.label.toLowerCase()}`}
				disabled={isSaving}
				onUpdate={() =>
					setContent(current => withNotebookUpdate(current, phase.id, index, update.entry))
				}
			/>
		)
	}

	const failed = (error, fallback) => {
		const message = networkAwareMessage(error, fallback)
		toast.error(message, { toastId: message, position: "top-center" })
	}

	const save = async () => {
		if (!canSave) return
		setSavingState(true)
		const fields = isExercise ? { level, note, text, schema } : { level, note, content }
		try {
			if (!saved) await addNotebookEntry(coach.coachID, { kind, title, phase, variants: [fields] })
			else
				await editNotebookEntry(saved.entryID, {
					kind,
					title,
					phase,
					variants: isNewVariant
						? withNewVariant(saved, fields)
						: withVariant(saved, base.variantID, fields),
				})
			closeModal()
		} catch (error) {
			failed(error, "Non sono riuscito a salvarlo nel quaderno")
			setSavingState(false)
		}
	}

	// con più varianti si toglie quella che si sta cambiando, e la voce resta
	const removesVariant = variantsCount > 1
	const remove = async () => {
		setSavingState(true)
		try {
			if (removesVariant)
				await editNotebookEntry(saved.entryID, {
					kind,
					title: saved.title,
					phase: saved.phase,
					variants: withoutVariant(saved, base.variantID),
				})
			else await deleteNotebookEntry(saved.entryID)
			closeModal()
		} catch (error) {
			failed(error, "Non sono riuscito a toglierlo dal quaderno")
			setSavingState(false)
		}
	}

	const heading = isNewVariant
		? "Nuova variante"
		: saved
			? saved.title
			: isExercise
				? "Nuovo esercizio"
				: "Nuova lezione nel quaderno"

	if (confirmingRemoval)
		return (
			<Container>
				<NotebookRemoveConfirm
					entry={saved}
					variant={removesVariant ? base : null}
					busy={isSaving}
					onConfirm={remove}
					onCancel={() => setConfirmingRemoval(false)}
				/>
			</Container>
		)

	return (
		<Container>
			<Text textSize="title" textWeight="bold" textColor={theme.colors.gunmetal}>
				{heading}
			</Text>
			<Text textColor={theme.colors.text_light} m={{ b: "0.75rem" }}>
				{isNewVariant
					? `Di «${saved.title}». Si parte dalla variante ${base.level ? levelLabel(base.level) : "senza livello"}: cambia quello che serve.`
					: saved
						? `${isExercise ? "Esercizio" : "Lezione"} del tuo quaderno · ${usageLabel(saved).toLowerCase()}`
						: "Solo tuo, finché non lo condividi coi colleghi"}
			</Text>

			<Div maxH="58vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				{!isNewVariant && (
					<TextInput
						value={title}
						handleChange={e => setTitle(e.target.value.slice(0, NOTEBOOK_TITLE_MAX))}
						placeholder={isExercise ? "Nome dell'esercizio" : "Nome della lezione"}
						aria-label="Nome"
					/>
				)}

				<Paragraph title="Per che livello:" m={{ t: "0.75rem", b: "0.5rem" }}>
					<LevelChips
						value={level}
						used={taken}
						canClear={canClearLevel}
						disabled={isSaving}
						onChange={setLevel}
					/>
					{taken.some(Boolean) && (
						<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.375rem" }}>
							Sbiaditi i livelli che hanno già la loro variante.
						</Text>
					)}
				</Paragraph>

				{showNote && (
					<Paragraph title="Cosa cambia rispetto alle altre:" m={{ t: "1rem", b: "0.5rem" }}>
						<TextInput
							value={note}
							handleChange={e => setNote(e.target.value.slice(0, NOTEBOOK_NOTE_MAX))}
							placeholder="Es. palla verde, campo intero"
							aria-label="Cosa cambia rispetto alle altre"
							m="0"
						/>
					</Paragraph>
				)}

				{isExercise ? (
					<>
						{!isNewVariant && (
							<Paragraph title="Per quale fase:" m={{ t: "0.75rem", b: "0.5rem" }}>
								<Div d="flex" flexWrap="wrap" style={{ gap: "0.5rem" }}>
									{[{ id: null, label: "Qualsiasi fase" }, ...PHASES].map(option => (
										<Chip
											key={option.id ?? "any"}
											text={option.label}
											isSelected={phase === option.id}
											disabled={isSaving}
											onClick={() => setPhase(option.id)}
										/>
									))}
								</Div>
							</Paragraph>
						)}
						<Paragraph title="L'esercizio:" m={{ t: "1.25rem", b: "0.5rem" }}>
							<textarea
								aria-label="L'esercizio"
								rows={4}
								value={text}
								disabled={isSaving}
								placeholder="Es. rovescio lungolinea da cesto: impatto avanti, chiusura sopra la spalla"
								onChange={e => setText(e.target.value.slice(0, NOTEBOOK_TEXT_MAX))}
								style={{
									width: "100%",
									boxSizing: "border-box",
									resize: "vertical",
									border: 0,
									borderRadius: "15px",
									padding: "0.75rem 1rem",
									background: theme.colors.surface,
									font: "inherit",
									lineHeight: 1.4,
									color: theme.colors.text,
								}}
							/>
						</Paragraph>
						<Paragraph title="Lo schema:" m={{ t: "1.25rem", b: "0.5rem" }}>
							{schema ? (
								<SchemaCard
									schema={schema}
									disabled={isSaving}
									onEdit={origin => setDrawing({ origin })}
									onRemove={() => setSchema(null)}
								/>
							) : (
								<Button
									w="100%"
									h="2.75rem"
									bg={theme.colors.surface}
									rounded="15px"
									textColor={theme.colors.gunmetal}
									style={{ gap: "0.5rem" }}
									disabled={isSaving}
									onClick={() => setDrawing({})}>
									<CourtIcon />
									<Text tag="span" textWeight="600">
										Disegna lo schema
									</Text>
								</Button>
							)}
						</Paragraph>
						{drawing && (
							<SchemaEditor
								schema={schema}
								title={title || text}
								origin={drawing.origin}
								onCancel={() => setDrawing(null)}
								onDone={drawn => {
									setSchema(drawn)
									setDrawing(null)
								}}
							/>
						)}
					</>
				) : (
					<Div m={{ t: "0.75rem" }}>
						<LessonContentFields
							content={content}
							setContent={setContent}
							disabled={isSaving}
							exerciseNote={exerciseNote}
						/>
					</Div>
				)}

				{/* una pastiglia rossa tenue, che chiede conferma */}
				{saved && !isNewVariant && (
					<Div d="flex" justify="center" m={{ t: "1rem", b: "0.5rem" }}>
						<Button
							h="2.5rem"
							p={{ x: "1rem" }}
							bg={theme.colors.red_light}
							rounded={theme.rounded["20px"]}
							textColor={theme.colors.red}
							textWeight="600"
							style={{ gap: "0.5rem" }}
							disabled={isSaving}
							onClick={() => setConfirmingRemoval(true)}>
							<FontAwesomeIcon icon={faTrashAlt} />
							{removesVariant ? `Togli la variante ${levelLabel(base.level)}` : "Togli dal quaderno"}
						</Button>
					</Div>
				)}
			</Div>

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text="Annulla"
							textSize="subheader"
							action={closeModal}
						/>
					</Col>
					<Col p="0">
						<PrimaryButton
							m="auto"
							bg={theme.colors.primary}
							textColor="white"
							textSize="subheader"
							text={isSaving ? "Sto salvando..." : "Salva"}
							disabled={!canSave}
							action={save}
						/>
					</Col>
				</Row>
			</Div>
		</Container>
	)
}

export default NotebookEntryModal
